import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LuFileText, LuClock4 } from "react-icons/lu";
import { useDispatch, useSelector } from "react-redux";
import { getPublishedList } from "../../../../../../store/actions/cms/scheduler";
import { FiEdit } from "react-icons/fi";
import Loading from "../../../../utils/loader";
import { NoDataFound } from '../../../../utils/noDataFound';

const PublishedContent = (props) => {
    const dispatch = useDispatch();
    const [hasNextPage, setHasNextPage] = useState(false);
    const [groupedData, setGroupedData] = useState({});
    const [data, setData] = useState([]);
    const observerRef = useRef(null); // Ref for the intersection observer
    const activeTab = localStorage.getItem('activeContentTab');
    const [isFetching, setIsFetching] = useState(false);

    const { publishedListLoading, publishedListData, publishedListError } = useSelector((state) => {
        return (state?.contentSchedularReducer);
    });

    const groupDataByDate = useCallback((items) => {
        const grouped = {};
        items.forEach(item => {
            const dateStr = item.updated_at?.split(',')[0] || "No Date";
            if (!grouped[dateStr]) {
                grouped[dateStr] = [];
            }
            grouped[dateStr].push(item);
        });

        Object.keys(grouped).forEach(date => {
            grouped[date].sort((a, b) => {
                const timeA = a.updated_at ? new Date(`${date}, ${a.updated_at.split(', ')[1]}`) : 0;
                const timeB = b.updated_at ? new Date(`${date}, ${b.updated_at.split(', ')[1]}`) : 0;
                return timeA - timeB;
            });
        });

        return grouped;
    }, []);

    // updating data for pagination
    useEffect(() => {
        if (publishedListData) {
            if (publishedListData.current === 1) {
                // Reset data for page 1
                const uniqueData = publishedListData?.results?.filter(
                    (item, index, self) => index === self.findIndex((t) => t.id === item.id)
                );
                setData(uniqueData);
            } else {
                // Append data for subsequent pages
                setData((prevData) => {
                    const mergedData = [...prevData, ...publishedListData?.results];
                    const uniqueData = mergedData.filter(
                        (item, index, self) => index === self.findIndex((t) => t.id === item.id)
                    );
                    return uniqueData;
                });
            }

            setHasNextPage(publishedListData.next !== null);
            props.setPublishedCurrentPage(publishedListData.current);
        }
    }, [publishedListData]);




    // grouping of data by date and updating state to show card counter
    useEffect(() => {
        if (data) {
            props.setPublishedLength(data?.length);
            setGroupedData(groupDataByDate(data));
        }
        return () => {
            localStorage?.removeItem('filterApplied')
        }
    }, [data, groupDataByDate]);

    // to call APi with next page
    const fetchNextPage = async () => {
        if (hasNextPage && !publishedListLoading && !isFetching) {
          setIsFetching(true);
          let FilterApplied = JSON.parse(localStorage.getItem('filterApplied'));
          await dispatch(getPublishedList({ 
            page: props.publishedCurrentPage + 1, 
            page_size: 12, 
            ...FilterApplied 
          }));
          setIsFetching(false);
        }
      };

    // to observe interaction with observerRef (observation to scroll)
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    // console.log("intersecting")
                    fetchNextPage();
                }
            },
            { threshold: 0.5 }
        );

        if (observerRef.current) {
            observer.observe(observerRef.current);
        }

        return () => {
            if (observerRef.current) {
                observer.unobserve(observerRef.current);
            }
        };
    }, [observerRef, hasNextPage, props.publishedCurrentPage, publishedListLoading]);


    const ContentCard = ({ item, index }) => (
        <div className='queue_block'>
            <div className="">
                <div className="card-body">
                    <div className="d-flex justify-content-between mb-3">
                        <div className="d-flex align-items-center">
                            <div className={`badges green_badge`}>
                                <LuFileText />
                            </div>
                            <div>
                                <div className='text_type'>TYPE</div>
                                <div className='text_head'>{item.content_type?.charAt(0).toUpperCase() + item.content_type.slice(1)}</div>
                            </div>
                        </div>
                        {item.updated_at &&
                            <div className={'schelule_at'} style={{ color: '#21904a', backgroundColor: '#f1fcf4', border: 'solid 1px #c0f2d2' }}>
                                <LuClock4 className="w-4 h-4" />
                                <span>Published at {new Date(item.updated_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, })}</span>
                            </div>
                        }
                    </div>
                    <div className='queue_body_title'>{item?.content_title}</div>
                    <div className='queue_body_desc' dangerouslySetInnerHTML={{ __html: item?.content_story }} />
                    <hr style={{ opacity: 0.1 }} />
                    <div className='queue_footer d-flex justify-content-between'>
                        <div className='d-flex' style={{ gap: '32px', alignItems: 'center' }}>
                            {item?.scheduled_by && <div><div className='text_type'>SCHEDULED BY</div><div className='text_value'>{item?.scheduled_by?.charAt(0).toUpperCase() + item.scheduled_by.slice(1)}</div></div>}
                            {item?.author && <div><div className='text_type'>AUTHOR</div><div className='text_value'>{item?.author?.charAt(0).toUpperCase() + item.author.slice(1)}</div></div>}
                        </div>
                        <div className='d-flex' style={{ gap: '16px', alignItems: 'center' }}>
                            <div className='reschedule' onClick={() => (window !== 'undefined') && window?.open(item?.draft_edit_url, '_blank')}>
                                <FiEdit />
                                <span>Edit Version</span>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );

    const formatDateHeader = (dateStr) => {
        const today = new Date();
        const targetDate = new Date(dateStr);

        if (targetDate.toDateString() === today.toDateString()) {
            return `Today: ${dateStr}`;
        }

        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        if (targetDate.toDateString() === tomorrow.toDateString()) {
            return `Tomorrow: ${dateStr}`;
        }

        return dateStr;
    };


    return (
        <div className="scheduler_custom_container mb-5">

            {(publishedListLoading && publishedListData?.results?.length === 0 && props.publishedCurrentPage == 1) && (
                <div className="flex justify-center items-center py-8">
                    <Loading className="draft_loader" />
                </div>
            )}

            {(publishedListData?.results?.length === 0 && !publishedListLoading ) && (
                <div className="my-5 ">
                    <NoDataFound />
                </div>
            )}

            {(Object.keys(groupedData).length > 0 && publishedListData?.results?.length>0) && (
                Object.keys(groupedData)
                    .sort((a, b) => new Date(b) - new Date(a))
                    .map((date, index) => (
                        <div key={index} className="mb-5">
                            <div className="d-flex align-items-center mb-2">
                                <h2 className="queue_bloc_date">{formatDateHeader(date)}</h2>
                            </div>

                            <div>
                                {groupedData[date]?.map((item, ind) => (
                                    <ContentCard key={item.id} item={item} index={index} />
                                ))}
                            </div>
                        </div>
                    ))
            )}

            {(publishedListLoading && props.publishedCurrentPage != 1) && (
                <div className="text-center py-4">
                    <Loading className="" />
                </div>
            )}

            {/* as discussed with design team this is not required */}
            {/* {!hasNextPage && data.length > 0 && (
                <div className="text-center py-4 no_more_queue">
                    <p>No more content to load</p>
                </div>
            )} */}

            {/* Intersection Observer Target */}
            <div ref={observerRef} style={{ height: '1px' }}></div>
        </div>
    );
};

export default PublishedContent;